<script lang="ts">
	/**
	 * `/Invoices` — the invoice register (Design §4.6).
	 *
	 * **Why this is a page and not a second door onto the Reports one.** The Documents panel there
	 * is read off the ledger, whose invoice arm is `status = 'paid'` — so it lists the paperwork
	 * behind money that *moved*, which is exactly right for a financial report and blind by
	 * construction to every invoice that has not been paid. A draft, one awaiting payment, one
	 * voided, one written off: none of them appeared on any admin screen at all, and "what is still
	 * owing" was a question this system could not answer. That is the gap, and the status filter is
	 * the control that closes it.
	 *
	 * Everything else is deliberately the same as the reports page, because it is the same filter
	 * and the same reader. **One bar, reflected into the URL**, so a slice can be sent to somebody;
	 * every control labelled through `FormField` and grouped, because the grouping is half the
	 * label; a narrowed control carrying an accent ring with the count stated beside Clear. The bar
	 * is literally the same component rather than a copy — a second copy of eleven conditions is a
	 * screen that quietly filters on something else while still rendering a plausible list.
	 *
	 * Two of the five kind toggles are withheld: a refund is not an invoice, and a toggle that can
	 * only ever return nothing reads as "there are none of these" rather than as an inapplicable
	 * control.
	 *
	 * The figures above the table are **per currency and never summed across them**, and they are of
	 * everything the filter matches rather than of the page in hand — a total that counted only what
	 * is on screen would change as somebody scrolled.
	 *
	 * The rows render through `invoiceReference` and `invoiceStatusLabel`, the same two helpers the
	 * customer's own history uses. That is not tidiness: there are two number columns on an invoice
	 * and only one of them is on the paper the customer is holding, and a refund leaves the
	 * provider's status at `paid` forever — so a second implementation here is a support
	 * conversation where the agent and the customer are reading different numbers and different
	 * words for the same document.
	 */

	import { onMount } from 'svelte';
	import {
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		EmptyState,
		FormField,
		InfiniteLoadElement,
		InvoiceDocumentLinks,
		PageHeader,
		Panel,
		RoutedLink,
		SelectField,
		TableRowsPlaceholder
	} from '../../components';
	import FilterBar from './reports/FilterBar.svelte';
	import { getReportFilters, listAdminInvoices } from '../../AppNetwork';
	import { formatAmount } from '../../stores/storefront';
	import {
		INVOICE_STATUS_COPY,
		formatCalendarDate,
		invoicePeriodLabel,
		invoiceReference,
		invoiceStatusLabel
	} from '../../Utilities';
	import type {
		AdminInvoiceRecord,
		AdminInvoiceQuery,
		AdminInvoiceTotals,
		ReportEventKind,
		ReportFilterOptions
	} from '../../AppTypes';

	const PAGE_SIZE = 25;

	/**
	 * All five of the ledger's kinds. Three are what an invoice was *for*; a refund and a chargeback
	 * are not invoices but things that happened to one, so on this page those two toggles match the
	 * invoices that were refunded or charged back — the server's `registerConditions` — and ticking
	 * them beside a kind widens the list rather than narrowing it. They were withheld once, which
	 * left no way at all to list the refunded invoices.
	 */
	const INVOICE_KINDS: ReportEventKind[] = ['sale', 'renewal', 'adjustment', 'refund', 'chargeback'];

	const INVOICE_KIND_NAMES = {
		refund: { name: 'Refunded', help: 'Invoices with money given back against them, in full or in part.' },
		chargeback: { name: 'Charged back', help: 'Invoices the customer disputed through their bank.' }
	};

	/**
	 * The status filter, in the **provider's** words, because that is what the column holds.
	 *
	 * There is deliberately no "Refunded" option, exactly as there is none on the customer's own
	 * list: a refund is not an invoice status — it moves the charge and leaves the invoice `paid` —
	 * so the server has nothing to filter on, and an option that quietly returned every paid invoice
	 * would be worse than its absence. The rows say which ones were refunded.
	 */
	const STATUS_OPTIONS = [
		{ value: '', name: 'Every status', description: 'No narrowing by status.' },
		{ value: 'open', name: INVOICE_STATUS_COPY.open, description: 'Issued and not yet paid — either just raised or being retried after a failed payment.' },
		{ value: 'paid', name: INVOICE_STATUS_COPY.paid, description: 'Settled. Still reads paid after a refund, which moves the charge rather than the invoice.' },
		{ value: 'draft', name: INVOICE_STATUS_COPY.draft, description: 'Being assembled by the provider and not yet issued; a renewal sits here for about an hour.' },
		{ value: 'void', name: INVOICE_STATUS_COPY.void, description: 'Cancelled before payment; nothing is owed on it.' },
		{ value: 'uncollectible', name: INVOICE_STATUS_COPY.uncollectible, description: 'Written off after retries ran out; the provider stopped trying.' }
	];

	/** Thirty days — the same default as Reports and the Sales Funnel, so leaving a page and returning never changes the window. */
	function fnDefaultFilter(): AdminInvoiceQuery {
		const to = new Date();
		return {
			from: new Date(to.getTime() - 30 * 86400000).toISOString(),
			to: to.toISOString(),
			sort: 'recent'
		};
	}

	let filter: AdminInvoiceQuery = fnDefaultFilter();
	let options: ReportFilterOptions | null = null;

	let invoices: AdminInvoiceRecord[] = [];
	let totals: AdminInvoiceTotals[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadingMore: boolean = false;
	let loadError: string = '';

	/** The paging sentinel. Typed structurally — only its two re-arm methods are used here. */
	let sentinel: { resetVisibilityTracking: () => void; updateState: () => void } | undefined;

	/**
	 * Read the filter out of the URL on arrival.
	 *
	 * `kind` is collected rather than assigned, because it is the one repeated parameter — a
	 * `for…of` over `entries()` would leave the last one standing and silently narrow a shared link
	 * to a single kind.
	 */
	onMount(() => {
		const parameters = new URLSearchParams(window.location.search);
		const fromURL: AdminInvoiceQuery = { ...fnDefaultFilter() };
		const kinds = parameters.getAll('kind').filter(value => value.length > 0);
		for (const [key, value] of parameters.entries()) {
			if (value.length === 0 || key === 'kind') continue;
			if (key === 'minAmount') {
				const parsed = Number.parseInt(value, 10);
				if (Number.isFinite(parsed)) fromURL.minAmount = parsed;
				continue;
			}
			(fromURL as Record<string, unknown>)[key] = value;
		}
		if (kinds.length > 0) fromURL.kind = kinds as ReportEventKind[];
		filter = fromURL;
		void fnLoadOptions();
	});

	async function fnLoadOptions(): Promise<void> {
		try {
			options = await getReportFilters();
		} catch {
			// The bar degrades to its dates, its kind toggles, the status filter and its two
			// free-text fields. A list of choices that could not be fetched must not cost the page
			// the register behind it.
			options = null;
		}
	}

	/**
	 * Keyed on a string, for the reason the report panels' loads are: a reactive statement reading
	 * the object directly re-runs on its own writes and fetches for ever.
	 */
	$: queryKey = JSON.stringify(filter);
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string, append: boolean = false): Promise<void> {
		// The sentinel is re-armed after every append, and in a tall viewport it is still on screen
		// when that happens — so without this guard one scroll would keep paging past the end of
		// the list, appending the last page over and over.
		if (append && (loading || loadingMore || invoices.length >= total)) return;

		if (append) {
			loadingMore = true;
		} else {
			loading = true;
			invoices = [];
		}
		loadError = '';

		try {
			const page = await listAdminInvoices(filter, PAGE_SIZE, append ? invoices.length : 0);
			invoices = append ? [...invoices, ...page.items] : page.items;
			total = page.total;
			totals = page.totals;
		} catch (error) {
			loadError = error instanceof Error ? error.message : 'The invoices could not be loaded';
			if (!append) {
				invoices = [];
				totals = [];
				total = 0;
			}
		} finally {
			loading = false;
			loadingMore = false;
			sentinel?.resetVisibilityTracking();
			sentinel?.updateState();
		}
	}

	/**
	 * Reflect the filter into the address bar.
	 *
	 * `history.replaceState` rather than the router's `push`, because changing a filter is not a
	 * navigation: pushing would make "back" mean "undo one filter change", which is not what
	 * anybody presses it for.
	 */
	function fnFilterChanged(event: CustomEvent<AdminInvoiceQuery>): void {
		filter = event.detail;
		const parameters = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value === undefined || value === null || value === '' || value === false) continue;
			if (Array.isArray(value)) {
				for (const entry of value) parameters.append(key, String(entry));
				continue;
			}
			parameters.set(key, String(value));
		}
		window.history.replaceState({}, '', `${window.location.pathname}?${parameters.toString()}`);
	}

	function fnStatus(event: CustomEvent<{ value: string }>): void {
		fnFilterChanged(
			new CustomEvent('change', {
				detail: { ...filter, status: event.detail.value === '' ? undefined : event.detail.value }
			})
		);
	}

	/**
	 * A money figure, formatted from the currency's own row.
	 *
	 * Never `/100`: that is right for USD and a hundred times wrong for JPY, and it is wrong
	 * silently because the figure still looks like money. The fallback names the units rather than
	 * printing a bare number, so a currency the store has since disabled is legible instead of
	 * misleading.
	 */
	function fnMoney(amount: number | null, currency: string): string {
		if (amount === null) return '—';
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	/** The ring on a narrowed control, as whole literal classes — see `FilterBar`. */
	$: statusActive = filter.status !== undefined && filter.status !== '';
</script>

<PageHeader title="Invoices">
	<svelte:fragment slot="description">
		Every invoice the store has raised, for every customer — including the ones nobody has paid.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<p class="mb-2">
			This is the register rather than a report: it lists invoices in <em>every</em> status, so a
			draft, one awaiting payment, one voided and one written off are all here. The Reports page's
			document list reads the ledger and therefore only ever sees invoices that were paid.
		</p>
		<p class="mb-2">
			An invoice serves the store's own PDF if and only if the store issued the document. Where
			the payment provider issued it, the row links to their copy instead — one charge with two
			documents carrying two numbers is worse than either alone.
		</p>
		<p>
			A refund does not move the provider's invoice status, so the state in the Status column is
			the invoice's own state combined with what has gone back out. The filter is in the address
			bar, so a slice can be sent to somebody.
		</p>
	</svelte:fragment>
</PageHeader>

<!-- `min-w-0`, because this page holds a wide table: without it the table's min-content width
     becomes the column's floor and the *page* grows sideways instead of the table scrolling. -->
<div class="space-y-6 min-w-0">
	<FilterBar
		{filter}
		{options}
		busy={loading}
		kinds={INVOICE_KINDS}
		kindLabel="What it was for, and what happened to it"
		kindNames={INVOICE_KIND_NAMES}
		showGranularity={false}
		extraActive={statusActive ? 1 : 0}
		on:change={fnFilterChanged}
	>
		<svelte:fragment slot="filters">
			<div class="pt-3 border-t border-widget-color">
				<div class="card-label mb-2">
					<i class="fa-solid fa-file-invoice text-[11px]" />The invoice itself
				</div>
				<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
					<FormField
						label="Status"
						id="invoices-status"
						helpText="The payment provider's own state for the invoice. A refund leaves the invoice paid at the provider, so refunded invoices are found with the Refunded toggle above rather than here."
					>
						<SelectField
							id="invoices-status"
							size="sm"
							class={statusActive ? '!border-primary-500 ring-1 ring-primary-500' : ''}
							value={filter.status ?? ''}
							items={STATUS_OPTIONS}
							on:change={fnStatus}
						/>
					</FormField>
				</div>
			</div>
		</svelte:fragment>
	</FilterBar>

	<!--
		Of everything the filter matches, never of the page in hand — a figure that counted only the
		rows on screen would change as somebody scrolled. One block per currency and no fourth
		figure adding them up: minor units in two currencies are different units.
	-->
	{#if !loading && loadError.length === 0 && totals.length > 0}
		<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
			{#each totals as entry (entry.currency)}
				<div class="widget bg-depth">
					<div class="card-label mb-2">
						<i class="fa-solid fa-coins text-[11px]" />{entry.currency.toUpperCase()}
					</div>
					<div class="text-2xl font-semibold tabular-nums">
						{fnMoney(entry.billed - entry.refunded, entry.currency)}
					</div>
					<div class="text-xs text-default mb-3">
						billed across {entry.invoices} invoice{entry.invoices === 1 ? '' : 's'}{entry.refunded > 0
							? ', net of refunds'
							: ''}
					</div>
					<dl class="text-sm space-y-1">
						<div class="flex justify-between gap-3">
							<dt class="text-default">Settled</dt>
							<dd class="tabular-nums">{fnMoney(entry.paid, entry.currency)}</dd>
						</div>
						<div class="flex justify-between gap-3">
							<dt class="text-default">Still owing</dt>
							<dd
								class="tabular-nums {entry.outstanding > 0
									? 'text-yellow-600 dark:text-yellow-400'
									: ''}"
							>
								{entry.outstanding === 0 ? '—' : fnMoney(entry.outstanding, entry.currency)}
							</dd>
						</div>
						<div class="flex justify-between gap-3">
							<dt class="text-default">Refunded</dt>
							<!-- Negated rather than prefixed with a minus, so the sign sits where
							     `formatMoney` puts it and the column cannot render "--€5.00". -->
							<dd class="tabular-nums">
								{entry.refunded === 0 ? '—' : fnMoney(-entry.refunded, entry.currency)}
							</dd>
						</div>
					</dl>
				</div>
			{/each}
		</div>
	{/if}

	<Panel title="Invoices" faIcon="file-invoice-dollar">
		<svelte:fragment slot="titleRight">
			{#if loadError.length === 0 && !loading}
				<span class="text-xs text-default">
					{total} invoice{total === 1 ? '' : 's'}
				</span>
			{/if}
		</svelte:fragment>

		{#if loadError.length > 0}
			<EmptyState variant="error" title="The invoices could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => void fnLoad(queryKey)}>
					Try again
				</Button>
			</EmptyState>
		{:else if !loading && invoices.length === 0}
			<EmptyState
				title="No invoices match this filter."
				description="Try widening the period, or clearing the filters above."
			/>
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Invoice</TableHeadCell>
					<TableHeadCell>Customer</TableHeadCell>
					<TableHeadCell>Issued</TableHeadCell>
					<TableHeadCell>Total</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Document</TableHeadCell>
				</TableHead>
				<TableBody>
					{#if loading}
						<TableRowsPlaceholder rows={8} columns={6} />
					{:else}
						{#each invoices as invoice (invoice.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<!-- Ours where we issued one, the provider's otherwise — one helper,
									     because the number on screen and the document behind the link
									     have to be the same piece of paper. -->
									<div>{invoiceReference(invoice)}</div>
									{#if invoice.productName !== null}
										<div class="text-xs text-default">{invoice.productName}</div>
									{/if}
									{#if invoicePeriodLabel(invoice).length > 0}
										<div class="text-xs text-default">{invoicePeriodLabel(invoice)}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if invoice.userUUID !== null}
										<!-- A customer is addressed by the **user's** uuid on the admin
										     side: one customer is one user, and it has no external
										     identifier of its own. -->
										<RoutedLink
											to={`/Customers/${invoice.userUUID}`}
											class="text-primary-500 hover:underline"
										>
											{invoice.customerName ?? invoice.customerEmail ?? 'Unnamed customer'}
										</RoutedLink>
									{:else}
										<span>{invoice.customerName ?? 'Unknown customer'}</span>
									{/if}
									{#if invoice.customerEmail !== null && invoice.customerEmail !== invoice.customerName}
										<div class="text-xs text-default">{invoice.customerEmail}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if invoice.issuedAt !== null}
										{formatCalendarDate(invoice.issuedAt)}
									{:else}
										<span class="text-default">Not issued yet</span>
									{/if}
								</TableBodyCell>
								<!-- `tabular-nums` for the reason every money column carries it: it is
								     what opts the cell out of the app-wide `overflow-wrap: anywhere`,
								     without which a narrow column renders a total as "€58.0 / 0". -->
								<TableBodyCell class="dataTable tabular-nums">
									{fnMoney(invoice.total, invoice.currency)}
									{#if invoice.status === 'open' && invoice.amountRemaining !== null && invoice.amountRemaining > 0}
										<div class="text-xs text-yellow-600 dark:text-yellow-400">
											{fnMoney(invoice.amountRemaining, invoice.currency)} still owing
										</div>
									{/if}
									{#if invoice.refundedAmount > 0}
										<div class="text-xs text-default">
											{fnMoney(-invoice.refundedAmount, invoice.currency)} refunded
										</div>
									{/if}
								</TableBodyCell>
								<!-- Never `status` alone: a refund leaves the provider's invoice `paid`
								     forever, so this is the one place the two facts are combined. -->
								<TableBodyCell class="dataTable">{invoiceStatusLabel(invoice)}</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!-- `admin` chooses the endpoint. It is a prop rather than something
									     inferred from the route, because guessing it 404s the day a
									     customer's list is rendered inside an admin page — and a 404
									     here looks exactly like a document that was never issued. -->
									<InvoiceDocumentLinks {invoice} />
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					{/if}
				</TableBody>
			</Table>

			{#if !loading && invoices.length < total}
				<InfiniteLoadElement
					bind:this={sentinel}
					class="w-full"
					on:becomeVisible={() => void fnLoad(queryKey, true)}
				>
					{#if loadingMore}
						<EmptyState variant="loading" compact title="Loading more…" />
					{/if}
				</InfiniteLoadElement>
			{/if}
		{/if}
	</Panel>
</div>
